import { InfoCard, Table } from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { githubAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BatchJobActionDialog } from './BatchJobActionDialog';
import { BatchJobDetailView } from './BatchJobDetailView';
import { useBatchEditorApi } from './useBatchEditorApi';
import {
  BatchJobWorkflowActionKind,
  BatchJobWorkflowFilter,
  BatchJobWorkflowItem,
  BatchJobWorkflowState,
  formatBatchUserRef,
  getWorkflowAction,
  mapLegacyJobToWorkflow,
  matchesWorkflowFilter,
  WORKFLOW_STATE_LABELS,
} from './batchJobWorkflow';

type LegacyBatchJob = Parameters<typeof mapLegacyJobToWorkflow>[0];

type BatchJobsPanelProps = {
  jobs: LegacyBatchJob[];
  loading: boolean;
  error?: Error;
  backendBaseUrl?: string;
  currentUserRef?: string;
  selectedJobId?: string;
  onSelectJob: (jobId: string) => void;
  onWorkflowChange?: () => void;
};

const operationLabels: Record<string, string> = {
  'create-missing-file': 'Create file',
  'edit-existing-file': 'Edit file',
  'edit-catalog-info': 'Edit catalog-info',
};

const FILTER_TABS: Array<{ value: BatchJobWorkflowFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending-approval', label: 'Pending approval' },
  { value: 'pending-merge', label: 'Pending merge' },
  { value: 'warning', label: 'Warning' },
  { value: 'complete', label: 'Complete' },
  { value: 'error', label: 'Error' },
  { value: 'canceled', label: 'Canceled' },
];

const useStyles = makeStyles(theme => ({
  filterTabs: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  workflowCell: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 120,
  },
  workflowButton: {
    minWidth: 108,
  },
  workflowButtonContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  detailPanel: {
    padding: theme.spacing(2, 3, 3),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function formatProgress(totals: BatchJobWorkflowItem['totals']): string {
  const total =
    totals.queued +
    totals.running +
    totals.skipped +
    totals.prOpened +
    totals.manualReview +
    totals.failed +
    (totals.merged ?? 0);
  if (total === 0) {
    return '—';
  }
  const parts: string[] = [];
  if (totals.merged > 0) {
    parts.push(`${totals.merged} merged`);
  }
  if (totals.prOpened > 0) {
    parts.push(`${totals.prOpened} PR`);
  }
  if (totals.manualReview > 0) {
    parts.push(`${totals.manualReview} review`);
  }
  if (totals.failed > 0) {
    parts.push(`${totals.failed} failed`);
  }
  return parts.length > 0 ? `${parts.join(', ')} · ${total} repos` : `${total} repos`;
}

function workflowStateChip(state: BatchJobWorkflowItem['workflowState']) {
  let color: 'default' | 'primary' | 'secondary' = 'default';
  if (state === 'draft' || state === 'pending-approval' || state === 'pending-merge') {
    color = 'primary';
  } else if (state === 'warning' || state === 'error' || state === 'canceled') {
    color = 'secondary';
  }
  return <Chip size="small" color={color} label={WORKFLOW_STATE_LABELS[state]} />;
}

function WorkflowActionButton({
  job,
  currentUserRef,
  onAction,
}: {
  job: BatchJobWorkflowItem;
  currentUserRef?: string;
  onAction: (job: BatchJobWorkflowItem, action: BatchJobWorkflowActionKind) => void;
}) {
  const classes = useStyles();
  const workflowAction = getWorkflowAction(job, currentUserRef);
  const enabled = workflowAction.mode === 'action' && workflowAction.enabled;
  const disabledReason = workflowAction.disabledReason;

  const button = (
    <Button
      size="small"
      className={classes.workflowButton}
      variant={workflowAction.buttonVariant}
      color={workflowAction.buttonColor}
      disabled={workflowAction.mode === 'status' || !enabled}
      onClick={event => {
        event.stopPropagation();
        if (workflowAction.action) {
          onAction(job, workflowAction.action);
        }
      }}
    >
      <span className={classes.workflowButtonContent}>
        {workflowAction.loading ? <CircularProgress size={14} /> : null}
        {workflowAction.label}
      </span>
    </Button>
  );

  if ((workflowAction.mode === 'status' || !enabled) && disabledReason) {
    return (
      <Tooltip title={disabledReason}>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
}

export function BatchJobsPanel({
  jobs,
  loading,
  error,
  backendBaseUrl,
  currentUserRef,
  selectedJobId,
  onSelectJob,
  onWorkflowChange,
}: BatchJobsPanelProps) {
  const classes = useStyles();
  const api = useBatchEditorApi();
  const githubAuth = useApi(githubAuthApiRef);
  const [filter, setFilter] = useState<BatchJobWorkflowFilter>('all');
  const [expandedJobId, setExpandedJobId] = useState<string | undefined>();
  const [dialogJob, setDialogJob] = useState<BatchJobWorkflowItem | undefined>();
  const [dialogAction, setDialogAction] = useState<BatchJobWorkflowActionKind | undefined>();
  const [dialogConfirmed, setDialogConfirmed] = useState(false);
  const [workflowSubmitting, setWorkflowSubmitting] = useState(false);
  const [workflowError, setWorkflowError] = useState<string | undefined>();

  const workflowJobs = useMemo(() => jobs.map(mapLegacyJobToWorkflow), [jobs]);

  const closeDialog = useCallback(() => {
    setDialogJob(undefined);
    setDialogAction(undefined);
    setDialogConfirmed(false);
    setWorkflowError(undefined);
  }, []);

  const openActionDialog = useCallback(
    (job: BatchJobWorkflowItem, action: BatchJobWorkflowActionKind) => {
      setDialogJob(job);
      setDialogAction(action);
      setDialogConfirmed(false);
      setWorkflowError(undefined);
    },
    [],
  );

  const handleDialogConfirm = useCallback(async () => {
    if (!dialogJob || !dialogAction || !dialogConfirmed || workflowSubmitting) {
      return;
    }

    if (!backendBaseUrl) {
      setWorkflowError('Batch editor backend is not configured');
      return;
    }

    setWorkflowSubmitting(true);
    setWorkflowError(undefined);
    try {
      const githubToken = await githubAuth.getAccessToken(['repo', 'read:user', 'user:email']);
      const endpoint =
        dialogAction === 'approve'
          ? `${backendBaseUrl}/jobs/${dialogJob.id}/approve`
          : `${backendBaseUrl}/jobs/${dialogJob.id}/merge`;
      await api.postJson(endpoint, {}, {
        headers: {
          'X-GitHub-Authorization': `Bearer ${githubToken}`,
        },
      });
      closeDialog();
      onWorkflowChange?.();
    } catch (submitError) {
      setWorkflowError(
        submitError instanceof Error ? submitError.message : 'Workflow action failed',
      );
    } finally {
      setWorkflowSubmitting(false);
    }
  }, [
    api,
    backendBaseUrl,
    closeDialog,
    dialogAction,
    dialogConfirmed,
    dialogJob,
    githubAuth,
    onWorkflowChange,
    workflowSubmitting,
  ]);

  const handleRowClick = useCallback(
    (_event: unknown, job: BatchJobWorkflowItem | undefined) => {
      if (!job) {
        return;
      }
      onSelectJob(job.id);
      setExpandedJobId(current => (current === job.id ? undefined : job.id));
    },
    [onSelectJob],
  );

  useEffect(() => {
    if (selectedJobId) {
      setExpandedJobId(selectedJobId);
    }
  }, [selectedJobId]);

  const counts = useMemo(() => {
    const states: BatchJobWorkflowState[] = [
      'draft',
      'pending-approval',
      'pending-merge',
      'warning',
      'canceled',
      'error',
      'complete',
    ];
    const byState = Object.fromEntries(
      states.map(state => [state, workflowJobs.filter(job => job.workflowState === state).length]),
    ) as Record<BatchJobWorkflowState, number>;
    return {
      all: workflowJobs.length,
      ...byState,
    };
  }, [workflowJobs]);

  const filteredJobs = useMemo(
    () => workflowJobs.filter(job => matchesWorkflowFilter(job, filter)),
    [filter, workflowJobs],
  );

  const detailPanelConfig = useMemo(
    () => ({
      tooltip: 'Show workflow status',
      render: ({ rowData }: { rowData: BatchJobWorkflowItem }) => (
        <Box className={classes.detailPanel}>
          <BatchJobDetailView job={rowData} />
        </Box>
      ),
    }),
    [classes.detailPanel],
  );

  const tableRows = useMemo(
    () =>
      filteredJobs.map(job => ({
        ...job,
        tableData: {
          showDetailPanel:
            job.id === expandedJobId ? detailPanelConfig.render : undefined,
        },
      })),
    [detailPanelConfig.render, expandedJobId, filteredJobs],
  );

  if (loading && workflowJobs.length === 0) {
    return (
      <InfoCard title="Batch jobs">
        <Typography variant="body2" color="textSecondary">
          Loading batch jobs…
        </Typography>
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Batch jobs">
        <Typography variant="body2" color="error">
          Could not load batch jobs: {error.message}
        </Typography>
      </InfoCard>
    );
  }

  return (
    <>
      <InfoCard
        title="Batch jobs"
        subheader="Each job has one workflow control that evolves from Open → Approve → Merge → Complete."
      >
        <Box className={classes.summaryRow}>
          <Chip size="small" variant="outlined" label={`${counts.all} total`} />
          <Chip size="small" color="primary" variant="outlined" label={`${counts['pending-approval'] ?? 0} pending approval`} />
          <Chip size="small" color="primary" variant="outlined" label={`${counts['pending-merge'] ?? 0} pending merge`} />
          <Chip size="small" color="secondary" variant="outlined" label={`${counts.warning ?? 0} warning`} />
          <Chip size="small" variant="outlined" label={`${counts.complete ?? 0} complete`} />
          <Chip size="small" variant="outlined" label={`${counts.error ?? 0} error`} />
        </Box>

        <Tabs
          className={classes.filterTabs}
          value={filter}
          onChange={(_event, value: BatchJobWorkflowFilter) => setFilter(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {FILTER_TABS.map(tab => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={
                tab.value === 'all'
                  ? `${tab.label} (${counts.all})`
                  : `${tab.label} (${counts[tab.value as BatchJobWorkflowState] ?? 0})`
              }
            />
          ))}
        </Tabs>

        {filteredJobs.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No jobs match this filter.
          </Typography>
        ) : (
          <Table
            options={{
              paging: true,
              pageSize: 15,
              pageSizeOptions: [10, 15, 25, 50],
              search: true,
              detailPanelType: 'single',
              rowStyle: (row: BatchJobWorkflowItem) =>
                row.id === selectedJobId ? { backgroundColor: 'rgba(63, 81, 181, 0.08)' } : {},
            }}
            onRowClick={handleRowClick}
            detailPanel={[detailPanelConfig]}
            columns={[
              { title: 'Title', field: 'title' },
              {
                title: 'State',
                field: 'workflowState',
                render: (row: BatchJobWorkflowItem) => workflowStateChip(row.workflowState),
              },
              {
                title: 'Operation',
                field: 'operation',
                render: (row: BatchJobWorkflowItem) =>
                  operationLabels[row.operation] ?? row.operation,
              },
              {
                title: 'Progress',
                field: 'totals',
                searchable: false,
                render: (row: BatchJobWorkflowItem) => formatProgress(row.totals),
              },
              {
                title: 'Created by',
                field: 'createdBy',
                render: (row: BatchJobWorkflowItem) => formatBatchUserRef(row.createdBy),
              },
              {
                title: 'Approved by',
                field: 'approvedBy',
                render: (row: BatchJobWorkflowItem) =>
                  row.approvedBy ? formatBatchUserRef(row.approvedBy) : '—',
              },
              {
                title: 'Merged by',
                field: 'mergedBy',
                render: (row: BatchJobWorkflowItem) =>
                  row.mergedBy ? formatBatchUserRef(row.mergedBy) : '—',
              },
              {
                title: 'Updated',
                field: 'updatedAt',
                render: (row: BatchJobWorkflowItem) => formatTimestamp(row.updatedAt),
              },
              {
                title: 'Workflow',
                searchable: false,
                render: (row: BatchJobWorkflowItem) => (
                  <Box className={classes.workflowCell}>
                    <WorkflowActionButton
                      job={row}
                      currentUserRef={currentUserRef}
                      onAction={openActionDialog}
                    />
                  </Box>
                ),
              },
            ]}
            data={tableRows}
          />
        )}
      </InfoCard>

      <BatchJobActionDialog
        open={Boolean(dialogJob && dialogAction)}
        action={dialogAction ?? 'approve'}
        job={dialogJob}
        confirmed={dialogConfirmed}
        submitting={workflowSubmitting}
        error={workflowError}
        onConfirmedChange={setDialogConfirmed}
        onClose={closeDialog}
        onConfirm={() => {
          void handleDialogConfirm();
        }}
      />
    </>
  );
}

export { matchesWorkflowFilter as matchesBatchJobFilter } from './batchJobWorkflow';
export type { BatchJobWorkflowItem as BatchJobListItem } from './batchJobWorkflow';
